import { z } from "zod";
import { instantSchema } from "./schemas";

/*
 * Runtime schemas of the month closing (T-171, ADR-0140). They mirror `backend/openapi.json` like `schemas.ts`.
 */

/** A month as `yyyy-MM`. */
export const monthSchema = z.string().regex(/^\d{4}-\d{2}$/u);

/** Why a person keeps a month from being closed. */
export const monthClosingBlockerSchema = z.enum([
  "OpenShift",
  "PendingCorrection",
  "PendingAbsence",
]);

/** A closing of a month for the tenant or an establishment. */
export const monthClosingSchema = z.object({
  id: z.uuid(),
  month: monthSchema,
  establishmentId: z.uuid().nullable(),
  status: z.enum(["Closed", "Reopened"]),
  closedAt: instantSchema,
  closedByEmployeeId: z.uuid(),
  reopenedAt: instantSchema.nullable(),
  reopenedByEmployeeId: z.uuid().nullable(),
  reopenReason: z.string().nullable(),
});

/** A change of a locked month by a correction approved after the closing. */
export const monthChangeNoticeSchema = z.object({
  id: z.uuid(),
  month: monthSchema,
  employeeId: z.uuid(),
  displayName: z.string(),
  personnelNumber: z.string(),
  correctionId: z.uuid(),
  approvedAt: instantSchema,
  approvedByEmployeeId: z.uuid(),
  acknowledgedAt: instantSchema.nullable(),
});

/** `GET /month-closings/{month}`, `:close` and `:reopen`. */
export const monthClosingOverviewSchema = z.object({
  month: monthSchema,
  establishmentId: z.uuid().nullable(),
  closing: monthClosingSchema.nullable(),
  closedForTenant: z.boolean(),
  isOver: z.boolean(),
  /** The month ended at most seven days ago: events held offline may still arrive (hint). */
  recentlyEnded: z.boolean().default(false),
  canClose: z.boolean(),
  personCount: z.number().int(),
  confirmedCount: z.number().int(),
  approvedCount: z.number().int(),
  openViolationCount: z.number().int(),
  blocked: z.array(
    z.object({
      employeeId: z.uuid(),
      displayName: z.string(),
      personnelNumber: z.string(),
      blockers: z.array(monthClosingBlockerSchema),
    }),
  ),
  history: z.array(monthClosingSchema),
  notices: z.array(monthChangeNoticeSchema),
});

/** Balances of a month shown with the confirmation and the approval. */
export const monthBalanceSchema = z.object({
  targetSeconds: z.number().int(),
  actualSeconds: z.number().int(),
  absenceCreditSeconds: z.number().int(),
  monthBalanceSeconds: z.number().int(),
  accountBalanceSeconds: z.number().int().nullable(),
});

/** Confirmation of the person and approval of a month. */
export const monthConfirmationSchema = z.object({
  state: z.enum(["None", "Confirmed", "Withdrawn"]),
  confirmedAt: instantSchema.nullable(),
  withdrawnAt: instantSchema.nullable(),
  comment: z.string().nullable(),
  approvedAt: instantSchema.nullable(),
  approvedByEmployeeId: z.uuid().nullable(),
  reservation: z.string().nullable(),
  approvedMonthBalanceSeconds: z.number().int().nullable(),
  approvedAccountBalanceSeconds: z.number().int().nullable(),
  approvalTextVersion: z.string().nullable().default(null),
});

/** `GET /me/months/{month}`, `:confirm` and `:withdraw`. */
export const ownMonthSchema = z.object({
  month: monthSchema,
  confirmationEnabled: z.boolean(),
  canConfirm: z.boolean(),
  isLocked: z.boolean(),
  confirmation: monthConfirmationSchema,
  balance: monthBalanceSchema.nullable(),
});

/** A person whose month the caller may approve. */
export const monthApprovalItemSchema = z.object({
  employeeId: z.uuid(),
  displayName: z.string(),
  personnelNumber: z.string(),
  confirmation: monthConfirmationSchema,
});

/** `GET /month-approvals/{month}`. */
export const monthApprovalListSchema = z.object({
  month: monthSchema,
  canApprove: z.boolean(),
  confirmationEnabled: z.boolean(),
  items: z.array(monthApprovalItemSchema),
  /**
   * Persons left out because their month has days under the AZV: under § 7 (8) AZV HR approves them (F-116); 0 from
   * an older server.
   */
  withheldUnderAzv: z.number().int().nonnegative().default(0),
});

/** `GET /month-approvals/{month}/employees/{employeeId}` and `:approve`. */
export const monthApprovalDetailSchema = z.object({
  month: monthSchema,
  item: monthApprovalItemSchema,
  balance: monthBalanceSchema,
  isLocked: z.boolean(),
});

/** `GET /month-change-notices`: open notices of every month. */
export const monthChangeNoticeListSchema = z.array(monthChangeNoticeSchema);

/** Why a person keeps a month from being closed. */
export type MonthClosingBlocker = z.infer<typeof monthClosingBlockerSchema>;
/** A closing of a month. */
export type MonthClosing = z.infer<typeof monthClosingSchema>;
/** A change of a locked month by an approved correction (notice to HR). */
export type MonthChangeNotice = z.infer<typeof monthChangeNoticeSchema>;
/** The state of the closing of a month for a scope. */
export type MonthClosingOverview = z.infer<typeof monthClosingOverviewSchema>;
/** Balances of a month. */
export type MonthBalance = z.infer<typeof monthBalanceSchema>;
/** Confirmation and approval of a month. */
export type MonthConfirmation = z.infer<typeof monthConfirmationSchema>;
/** The caller's own month. */
export type OwnMonth = z.infer<typeof ownMonthSchema>;
/** A person whose month the caller may approve. */
export type MonthApprovalItem = z.infer<typeof monthApprovalItemSchema>;
/** The months the caller may approve. */
export type MonthApprovalList = z.infer<typeof monthApprovalListSchema>;
/** The month of one person before the approval. */
export type MonthApprovalDetail = z.infer<typeof monthApprovalDetailSchema>;
